import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, X as XIcon, Pencil, Megaphone, Search, ToggleLeft, ToggleRight, Percent, Gift } from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

const emptyPromo = {
  kind: "free_item",
  title: "",
  banner_headline: "",
  subtitle: "",
  checkout_label: "",
  min_spend: 150,
  required_product_ids: [],
  free_product_id: "",
  free_quantity: 2,
  percent_off: 10,
  max_discount_amount: "",
  starts_at: "",
  ends_at: "",
  active: true,
  priority: 0,
};

const KindBadge = ({ kind }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
    kind === "free_item"
      ? "border-[#FFD166]/50 text-[#FFD166] bg-[#FFD166]/10"
      : "border-emerald-400/50 text-emerald-300 bg-emerald-500/10"
  }`}>
    {kind === "free_item" ? <><Gift className="w-3 h-3" /> Free Item</> : <><Percent className="w-3 h-3" /> % Off</>}
  </span>
);

const inWindow = (p) => {
  const now = new Date().toISOString();
  if (p.starts_at && now < p.starts_at) return "upcoming";
  if (p.ends_at) {
    const key = p.ends_at.length > 10 ? p.ends_at : p.ends_at + "T23:59:59+00:00";
    if (now > key) return "expired";
  }
  return "live";
};

const PromoForm = ({ draft, products, onClose, onSaved }) => {
  const [f, setF] = useState({ ...emptyPromo, ...draft, required_product_ids: draft?.required_product_ids || [] });
  const [saving, setSaving] = useState(false);
  const isNew = !draft?.id;

  const toggleRequired = (pid) => {
    setF((cur) => {
      const has = cur.required_product_ids.includes(pid);
      return {
        ...cur,
        required_product_ids: has ? cur.required_product_ids.filter((x) => x !== pid) : [...cur.required_product_ids, pid],
      };
    });
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Give the promo an internal title."); return; }
    if (!f.banner_headline.trim()) { toast.error("Banner headline is required — that's what customers see."); return; }
    if (!f.checkout_label.trim()) { toast.error("Checkout label is required."); return; }
    if (f.kind === "free_item" && !f.free_product_id) { toast.error("Pick which product is free."); return; }
    if (f.kind === "percent_off" && !(Number(f.percent_off) > 0)) { toast.error("Percent off must be greater than zero."); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        min_spend: Number(f.min_spend) || 0,
        free_quantity: Math.max(1, Number(f.free_quantity) || 1),
        percent_off: Math.max(0, Math.min(100, Number(f.percent_off) || 0)),
        priority: Number(f.priority) || 0,
        max_discount_amount: f.max_discount_amount !== "" && f.max_discount_amount !== null
          ? Math.max(0, Number(f.max_discount_amount) || 0) || null
          : null,
      };
      if (isNew) {
        await axios.post(`${API}/admin/promos`, payload);
      } else {
        await axios.patch(`${API}/admin/promos/${draft.id}`, payload);
      }
      toast.success("Promo saved");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" data-testid="promo-form-modal">
      <Card className="p-6 w-full max-w-3xl my-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">{isNew ? "New Promotion" : `Edit · ${draft.title}`}</h2>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]" data-testid="promo-form-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Type">
            <div className="flex gap-2">
              <button type="button" onClick={() => setF({ ...f, kind: "free_item" })} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${f.kind === "free_item" ? "border-[#FFD166] text-[#FFD166] bg-[#FFD166]/10" : "border-[#FFD166]/25 text-[#B9C8D5]"}`} data-testid="pf-kind-free">
                <Gift className="w-3.5 h-3.5 inline mr-1" /> Free Item
              </button>
              <button type="button" onClick={() => setF({ ...f, kind: "percent_off" })} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${f.kind === "percent_off" ? "border-emerald-400 text-emerald-300 bg-emerald-500/10" : "border-[#FFD166]/25 text-[#B9C8D5]"}`} data-testid="pf-kind-percent">
                <Percent className="w-3.5 h-3.5 inline mr-1" /> % Off
              </button>
            </div>
          </Field>
          <Field label="Internal title (admin only)">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. February Fan Freebie" data-testid="pf-title" />
          </Field>

          <Field label="Banner headline (shown to customers)">
            <Input value={f.banner_headline} onChange={(e) => setF({ ...f, banner_headline: e.target.value })} placeholder="Spend $150 and get 2 stroller fans FREE!" data-testid="pf-headline" />
          </Field>
          <Field label="Checkout line label">
            <Input value={f.checkout_label} onChange={(e) => setF({ ...f, checkout_label: e.target.value })} placeholder="2 Free Stroller Fans" data-testid="pf-checkout-label" />
          </Field>

          <Field label="Subtitle (optional)">
            <Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} placeholder="Add fans to your cart to unlock the deal." data-testid="pf-subtitle" />
          </Field>
          <Field label="Minimum spend ($)">
            <Input type="number" step="1" value={f.min_spend} onChange={(e) => setF({ ...f, min_spend: e.target.value })} data-testid="pf-min-spend" />
          </Field>

          {f.kind === "free_item" && (
            <>
              <Field label="Free product">
                <select value={f.free_product_id} onChange={(e) => setF({ ...f, free_product_id: e.target.value })} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="pf-free-product">
                  <option value="">Pick a product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Free quantity">
                <Input type="number" min="1" value={f.free_quantity} onChange={(e) => setF({ ...f, free_quantity: e.target.value })} data-testid="pf-free-qty" />
              </Field>
              <Field label="Max $ value of free items (optional)" hint="e.g. 2 fans free but no more than $40 off total">
                <Input type="number" min="0" step="0.01" placeholder="No limit" value={f.max_discount_amount} onChange={(e) => setF({ ...f, max_discount_amount: e.target.value })} data-testid="pf-max-discount" />
              </Field>
            </>
          )}
          {f.kind === "percent_off" && (
            <>
              <Field label="Percent off (%)">
                <Input type="number" min="0" max="100" step="1" value={f.percent_off} onChange={(e) => setF({ ...f, percent_off: e.target.value })} data-testid="pf-percent" />
              </Field>
              <Field label="Max $ discount cap (optional)" hint="e.g. 20% off but no more than $50 off">
                <Input type="number" min="0" step="0.01" placeholder="No limit" value={f.max_discount_amount} onChange={(e) => setF({ ...f, max_discount_amount: e.target.value })} data-testid="pf-max-discount" />
              </Field>
            </>
          )}

          <Field label="Starts at (optional)">
            <Input type="date" value={(f.starts_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, starts_at: e.target.value })} data-testid="pf-starts" />
          </Field>
          <Field label="Ends at (optional)">
            <Input type="date" value={(f.ends_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, ends_at: e.target.value })} data-testid="pf-ends" />
          </Field>

          <Field label="Priority (higher wins ties)">
            <Input type="number" step="1" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="pf-priority" />
          </Field>
          <Field label="Status">
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="pf-active">
              <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-[#FFD166]" />
              <span className="text-sm text-[#F6F1E4]">{f.active !== false ? "Live" : "Paused"}</span>
            </label>
          </Field>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Restrict to spend on these products (optional)</label>
          <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-[#FFD166]/15 rounded-lg" data-testid="pf-required-products">
            {products.map((p) => {
              const on = f.required_product_ids.includes(p.id);
              return (
                <button key={p.id} type="button" onClick={() => toggleRequired(p.id)} className={`text-[11px] px-2 py-1 rounded-full border ${on ? "border-[#FFD166] text-[#FFD166] bg-[#FFD166]/10" : "border-[#FFD166]/20 text-[#B9C8D5]"}`}>
                  {p.name}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[#B9C8D5]/70 mt-2">
            Leave empty to count spend across the whole cart. When set, all selected products must be in the cart and only their subtotals count toward the threshold.
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3 justify-end">
          <Btn variant="ghost" onClick={onClose} data-testid="pf-cancel">Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="pf-save">
            {saving ? "Saving…" : "Save promo"}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

const PromoCard = ({ p, onEdit, onDelete, onToggle }) => {
  const status = inWindow(p);
  return (
    <Card className={`p-4 flex flex-col gap-3 ${!p.active ? "opacity-60" : ""}`} data-testid={`promo-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <KindBadge kind={p.kind} />
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
              status === "live" && p.active ? "border-emerald-400/50 text-emerald-300" :
              status === "upcoming" ? "border-sky-400/50 text-sky-300" :
              status === "expired" ? "border-red-400/50 text-red-300" :
              "border-[#B9C8D5]/40 text-[#B9C8D5]"
            }`}>{p.active ? status : "paused"}</span>
          </div>
          <div className="font-serif text-lg leading-tight truncate" title={p.title}>{p.title}</div>
          <div className="text-xs text-[#B9C8D5] mt-1 line-clamp-2">{p.banner_headline}</div>
        </div>
      </div>

      <div className="text-[11px] text-[#B9C8D5] leading-relaxed">
        {p.kind === "free_item" ? (
          <>Spend ≥ <b className="text-[#FFD166]">${Number(p.min_spend).toFixed(0)}</b>{p.required_product_names?.length ? <> on <span className="text-[#F6F1E4]">{p.required_product_names.join(", ")}</span></> : ""} → <b className="text-[#FFD166]">{p.free_quantity}× {p.free_product_name || p.free_product_id}</b> free{p.max_discount_amount ? <span className="text-[#B9C8D5]/60"> (max ${Number(p.max_discount_amount).toFixed(0)} off)</span> : ""}</>
        ) : (
          <>Spend ≥ <b className="text-emerald-300">${Number(p.min_spend).toFixed(0)}</b>{p.required_product_names?.length ? <> on <span className="text-[#F6F1E4]">{p.required_product_names.join(", ")}</span></> : ""} → <b className="text-emerald-300">{p.percent_off}% off</b> the cart{p.max_discount_amount ? <span className="text-[#B9C8D5]/60"> (max ${Number(p.max_discount_amount).toFixed(0)} off)</span> : ""}</>
        )}
      </div>
      <div className="text-[10px] text-[#B9C8D5]/70">
        {p.starts_at ? `Starts ${p.starts_at.slice(0, 10)}` : "No start date"}
        {" · "}
        {p.ends_at ? `Ends ${p.ends_at.slice(0, 10)}` : "No end date"}
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid={`promo-stats-${p.id}`}>
        <div className="rounded-lg border border-[#FFD166]/15 bg-black/25 p-2.5">
          <div className="text-[9px] uppercase tracking-widest text-[#B9C8D5]/70">Redemptions</div>
          <div className="font-serif text-lg text-[#F6F1E4]" data-testid={`promo-redemptions-${p.id}`}>{Number(p.redemption_count || 0)}</div>
        </div>
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-2.5">
          <div className="text-[9px] uppercase tracking-widest text-emerald-200/80">Saved for guests</div>
          <div className="font-serif text-lg text-emerald-200" data-testid={`promo-savings-${p.id}`}>${Number(p.total_savings || 0).toFixed(2)}</div>
        </div>
      </div>

      <div className="pt-3 border-t border-[#FFD166]/15 flex items-center gap-1">
        <button onClick={onEdit} className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#FFD166]/20 text-[#B9C8D5] hover:border-[#FFD166]/50 hover:text-[#FFD166]" title="Edit" data-testid={`promo-edit-${p.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={onToggle} className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${p.active ? "border-emerald-400/40 text-emerald-300" : "border-[#B9C8D5]/30 text-[#B9C8D5]"}`} title={p.active ? "Pause" : "Activate"} data-testid={`promo-toggle-${p.id}`}>
          {p.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
        </button>
        <div className="flex-1" />
        <button onClick={onDelete} className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-500/25 text-red-400 hover:border-red-500/60 hover:bg-red-500/10" title="Delete" data-testid={`promo-delete-${p.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </Card>
  );
};

export const Promos = () => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const [p, prods, bundles] = await Promise.all([
      axios.get(`${API}/admin/promos`),
      axios.get(`${API}/admin/products`),
      axios.get(`${API}/admin/bundles`),
    ]);
    setItems(p.data.promos || []);
    const bundleProds = (bundles.data.bundles || []).map((b) => ({ id: b.id, name: `[Bundle] ${b.name}` }));
    const regular = (prods.data.products || []).filter((x) => x.active !== false).map((x) => ({ id: x.id, name: x.name }));
    setProducts([...regular, ...bundleProds]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this promo? Existing reservations that already applied it are unaffected.")) return;
    try {
      await axios.delete(`${API}/admin/promos/${id}`);
      toast.success("Promo deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggle = async (p) => {
    const next = !p.active;
    setItems((cur) => cur.map((x) => (x.id === p.id ? { ...x, active: next } : x)));
    try {
      await axios.patch(`${API}/admin/promos/${p.id}`, { active: next });
      toast.success(next ? "Promo activated" : "Promo paused");
      load();
    } catch (e) {
      setItems((cur) => cur.map((x) => (x.id === p.id ? { ...x, active: !next } : x)));
      toast.error(fmtErr(e.response?.data?.detail) || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.banner_headline || "").toLowerCase().includes(q));
  }, [items, query]);

  const totals = useMemo(() => items.reduce(
    (acc, p) => ({
      redemptions: acc.redemptions + Number(p.redemption_count || 0),
      savings: acc.savings + Number(p.total_savings || 0),
    }),
    { redemptions: 0, savings: 0 },
  ), [items]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Promotions</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">Automatic marketing deals shown across the storefront banner, Deals page, and checkout summary.</p>
        </div>
        <Btn onClick={() => setEditing({ ...emptyPromo })} data-testid="promo-add-btn"><Plus className="w-3.5 h-3.5" /> New Promo</Btn>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="promo-totals">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">Total promos</div>
          <div className="font-serif text-3xl text-[#F6F1E4] mt-1">{items.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">Total redemptions</div>
          <div className="font-serif text-3xl text-[#FFD166] mt-1" data-testid="promo-total-redemptions">{totals.redemptions}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-200/80">Total saved for guests</div>
          <div className="font-serif text-3xl text-emerald-200 mt-1" data-testid="promo-total-savings">${totals.savings.toFixed(2)}</div>
        </Card>
      </div>

      <div className="relative mb-6">
        <Search className="w-4 h-4 text-[#B9C8D5] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search promos…" className="pl-9" data-testid="promos-search" />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="promos-empty">
          <Megaphone className="w-8 h-8 text-[#B9C8D5] mx-auto mb-3 opacity-60" />
          <div className="text-[#F6F1E4] font-medium">No promotions yet</div>
          <div className="text-xs text-[#B9C8D5] mt-1">Click "New Promo" to create your first automatic marketing offer.</div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="promos-grid">
          {filtered.map((p) => (
            <PromoCard key={p.id} p={p} onEdit={() => setEditing({ ...p })} onDelete={() => del(p.id)} onToggle={() => toggle(p)} />
          ))}
        </div>
      )}

      {editing && (
        <PromoForm
          draft={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};
